import { useState } from "react";
import { openCta, submitEligibilityLead, APPLICATIONS_CLOSED } from "../../lib/penaltyRecoveryApi";

const OPTS = [
  { v: "yes", label: "Yes" },
  { v: "no", label: "No" },
  { v: "ns", label: "Not sure" },
];

const QUESTIONS = [
  { id: "q1", n: "Q1", text: "Between 2020 and 2023, did you file or pay any federal tax return late?" },
  { id: "q2", n: "Q2", text: "Did the IRS charge you a penalty or interest during that time?" },
  { id: "q3", n: "Q3", text: "Have those penalties already been refunded or removed?" },
];

// eligible if: filed/paid late (yes|ns) AND charged penalty (yes|ns) AND NOT already refunded (no|ns)
function computeEligible(a) {
  return ["yes", "ns"].includes(a.q1) && ["yes", "ns"].includes(a.q2) && ["no", "ns"].includes(a.q3);
}

const Dot = ({ active }) => (
  <span style={{ width: 15, height: 15, borderRadius: "50%", border: "1.5px solid #C7C2B4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    {active && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#00A4BF", display: "block" }} />}
  </span>
);

export default function PenaltyRecoveryFreeCheck() {
  const [answers, setAnswers] = useState({ q1: null, q2: null, q3: null });
  const [showResult, setShowResult] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);

  const allAnswered = answers.q1 && answers.q2 && answers.q3;
  const eligible = computeEligible(answers);

  const reset = () => {
    setAnswers({ q1: null, q2: null, q3: null });
    setShowResult(false);
    setEmail("");
    setSubscribed(false);
  };

  const subscribe = async () => {
    if (!email || saving) return;
    setSaving(true);
    try {
      await submitEligibilityLead({ email, answers, eligible });
      setSubscribed(true);
    } catch (e) {
      setSubscribed(true); // optimistic — capture is best-effort funnel signal
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="check" data-screen-label="Eligibility check" style={{ background: "#FAF8F3", borderBottom: "1px solid #EFEBE0" }} data-testid="pr-free-check">
      <div style={{ maxWidth: 740, margin: "0 auto", padding: "96px 36px" }} data-pad>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 14, fontSize: 13, fontWeight: 600, letterSpacing: "0.13em", color: "#B5993F", textTransform: "uppercase", marginBottom: 20 }}>
            <span style={{ width: 34, height: 1.5, background: "#B5993F" }} />Free 2-minute check<span style={{ width: 34, height: 1.5, background: "#B5993F" }} />
          </div>
          <h2 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontWeight: 500, fontSize: 40, lineHeight: 1.12, color: "#233853", marginBottom: 12, textWrap: "balance" }}>Not ready to start? Check in three questions.</h2>
          <p style={{ fontSize: 17, color: "#5C6776", maxWidth: 480, margin: "0 auto" }}>No account, no IRS contact — just an honest read on whether it's worth your time.</p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #E7E3D8", borderRadius: 16, padding: 40, boxShadow: "0 20px 50px -30px rgba(22,38,61,0.25)" }}>
          {showResult ? (
            <div style={{ textAlign: "center" }}>
              {eligible ? (
                <div>
                  <span style={{ display: "inline-flex", width: 56, height: 56, borderRadius: "50%", background: "rgba(0,164,191,0.12)", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                    <span style={{ color: "#00A4BF", fontSize: 28, fontWeight: 700 }}>✓</span>
                  </span>
                  <h3 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 600, fontSize: 28, color: "#233853", marginBottom: 12 }}>You may have recoverable penalties.</h3>
                  {APPLICATIONS_CLOSED ? (
                    <>
                      <p style={{ fontSize: 16.5, color: "#5C6776", lineHeight: 1.6, maxWidth: 480, margin: "0 auto 30px" }}>Based on your answers, penalties from that window may have been recoverable — but we're no longer accepting new applications, as the time left before the filing deadline is too short to responsibly prepare new claims. If you'd still like to speak with a CPA, leave your details and we'll reach out.</p>
                      <button onClick={() => openCta("review", "free_check_result")} data-testid="pr-check-result-cta" style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#233853", color: "#fff", fontWeight: 700, fontSize: 16, padding: "15px 28px", borderRadius: 8, border: "none", cursor: "pointer", font: "inherit", boxShadow: "0 4px 14px rgba(35,56,83,0.28)" }}>Talk to a CPA — Leave My Details <span>→</span></button>
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize: 16.5, color: "#5C6776", lineHeight: 1.6, maxWidth: 480, margin: "0 auto 30px" }}>Based on your answers, a CPA review is likely worth it. The $295 review shows the exact penalties and interest on your IRS account — and what's recoverable.</p>
                      <button onClick={() => openCta("review", "free_check_result")} data-testid="pr-check-result-cta" style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "linear-gradient(180deg,#C8AC43,#B08C24)", color: "#233853", fontWeight: 700, fontSize: 16, padding: "15px 28px", borderRadius: 8, border: "none", cursor: "pointer", font: "inherit", boxShadow: "0 4px 14px rgba(176,140,36,0.28)" }}>Start My Review — $295 <span>→</span></button>
                    </>
                  )}
                </div>
              ) : (
                <div>
                  <span style={{ display: "inline-flex", width: 56, height: 56, borderRadius: "50%", background: "#F1EFE8", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                    <span style={{ color: "#9098A4", fontSize: 26 }}>–</span>
                  </span>
                  <h3 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 600, fontSize: 28, color: "#233853", marginBottom: 12 }}>You likely don't have a claim.</h3>
                  <p style={{ fontSize: 16.5, color: "#5C6776", lineHeight: 1.6, maxWidth: 480, margin: "0 auto 24px" }}>From your answers, there probably isn't a recoverable penalty — but a CPA can confirm for certain if you're unsure.</p>
                </div>
              )}

              <div style={{ marginTop: 28, paddingTop: 26, borderTop: "1px solid #EFEDE6" }}>
                {subscribed ? (
                  <p style={{ fontSize: 15.5, color: "#233853", fontWeight: 500 }} data-testid="pr-check-subscribed">Thanks — we'll send your eligibility summary and any deadline reminders.</p>
                ) : (
                  <div>
                    <p style={{ fontSize: 15, color: "#5C6776", marginBottom: 14 }}>Prefer to think it over? Get your summary and deadline reminders by email.</p>
                    <div style={{ display: "flex", gap: 10, maxWidth: 420, margin: "0 auto" }}>
                      <input type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pr-check-email" style={{ flex: 1, padding: "13px 15px", border: "1px solid #DED9CC", borderRadius: 8, font: "inherit", fontSize: 15, color: "#233853", background: "#FCFBF8", outline: "none" }} />
                      <button onClick={subscribe} disabled={saving} data-testid="pr-check-email-submit" style={{ background: "#233853", color: "#fff", font: "inherit", fontWeight: 600, fontSize: 15, padding: "13px 22px", border: "none", borderRadius: 8, cursor: saving ? "default" : "pointer", whiteSpace: "nowrap", opacity: saving ? 0.7 : 1 }}>Email it</button>
                    </div>
                  </div>
                )}
              </div>
              <button onClick={reset} data-testid="pr-check-reset" style={{ marginTop: 22, background: "none", border: "none", color: "#9098A4", font: "inherit", fontSize: 13.5, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Start over</button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
              {QUESTIONS.map((q) => (
                <div key={q.id}>
                  <p style={{ fontSize: 17, color: "#233853", fontWeight: 600, marginBottom: 14 }}>
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", color: "#B5993F", fontSize: 15, marginRight: 8 }}>{q.n}</span>{q.text}
                  </p>
                  <div style={{ display: "flex", gap: 10 }} data-opt-row>
                    {OPTS.map((o) => (
                      <button key={o.v} onClick={() => setAnswers((a) => ({ ...a, [q.id]: o.v }))} data-testid={`pr-check-${q.id}-${o.v}`} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 9, padding: 13, border: `1px solid ${answers[q.id] === o.v ? "#B5993F" : "#E0DBCD"}`, borderRadius: 9, background: "#fff", cursor: "pointer", font: "inherit", fontSize: 15, fontWeight: 500, color: "#233853" }}>
                        <Dot active={answers[q.id] === o.v} />{o.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <button onClick={() => allAnswered && setShowResult(true)} disabled={!allAnswered} data-testid="pr-check-submit" style={{ width: "100%", background: allAnswered ? "#C8AC43" : "#EAE6DA", color: "#233853", font: "inherit", fontWeight: 700, fontSize: 16, padding: 15, border: "none", borderRadius: 9, cursor: allAnswered ? "pointer" : "default" }}>See my result</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
